// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useState, useEffect, useRef } from 'react';
import { CallProvider } from '@azure/communication-react';
import { Stack } from '@fluentui/react';
import * as styles from '../styles/OpenAiWidget.styles';
import { Call } from '@azure/communication-calling';
import { OpenAiControlBar } from './OpenAiControlBar';
import WaveAnimation from './WaveAnimation';
import InerviewStepper from './InterviewStepper';

type AgentMessage = {
  id: string;
  text: string;
  timestamp: string;
  sender: 'agent' | 'user';
};

interface OpenAiWidgetCallScreenProps {
  currentCall: Call;
  onHangup: () => void;
  messages?: AgentMessage[];
}

const TranscriptIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="8" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="12" y2="14" />
    <line x1="8" y1="6" x2="16" y2="6" />
  </svg>
);

const CloseIcon = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const OpenAiWidgetCallScreen = ({
  currentCall,
  onHangup,
  messages = []
}: OpenAiWidgetCallScreenProps): JSX.Element => {
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const transcriptContentRef = useRef<HTMLDivElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const agentMessages = messages.filter((m) => m.sender === 'agent');
    if (agentMessages.length > 0) {
      setCurrentIndex(agentMessages.length - 1);
    }
  }, [messages]);

  useEffect(() => {
    if (showTranscriptPanel && transcriptContentRef.current) {
      transcriptContentRef.current.scrollTop = transcriptContentRef.current.scrollHeight;
    }
  }, [showTranscriptPanel, messages]);

  const agentMessages = messages.filter((m) => m.sender === 'agent');
  const currentMessage = agentMessages[currentIndex] ?? agentMessages[agentMessages.length - 1];
  const hasBulletPoints = currentMessage ? /[•\-*]|\n\s*[•\-*]/.test(currentMessage.text) : false;
  const messageStyle = hasBulletPoints ? styles.messageTextWithBulletsStyle : styles.messageTextStyle;

  return (
    <CallProvider call={currentCall}>
      <Stack style={styles.agentSpeakingCardStyle}>
        <div style={styles.waveContainerStyle}>
          <WaveAnimation />
        </div>

        <div style={styles.messageWrapperStyle}>
          <div ref={messageListRef} style={styles.messageScrollContainerStyle}>
            {messages.filter((m) => m.sender === 'agent').length === 0 ? (
              <p style={{ margin: 0, color: '#605e5c' }}>Waiting for assistant response…</p>
            ) : (
              (() => {
                const agentMessages = messages.filter((m) => m.sender === 'agent');
                const currentMessage = agentMessages[currentIndex] ?? agentMessages[agentMessages.length - 1];
                if (!currentMessage) {
                  return null;
                }
                return (
                  <Stack
                    key={currentMessage.id}
                    className="agent-question-text-container"
                    style={styles.slideInRightStyle}
                    styles={{
                      root: {
                        animation: 'slideInRight 0.4s ease',
                        backgroundColor: 'transparent',
                        padding: 12,
                        width: '100%',
                        maxWidth: '100%'
                      }
                    }}
                  >
                    <p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '22px', color: '#004140' }}>
                      Assessment Agent
                    </p>
                    {/* <p style={{ margin: 0, fontSize: '18px', color: '#004140', whiteSpace: 'pre-wrap' }}>
                      {currentMessage.text}
                    </p> */}
                    <p style={messageStyle}>{currentMessage.text}</p>
                  </Stack>
                );
              })()
            )}
          </div>
        </div>

        <div style={styles.callScreenBottomBarStyle}>
          <button type="button" style={styles.showTranscriptButtonStyle} onClick={() => setShowTranscriptPanel(true)}>
            <TranscriptIcon />
            Show transcript
          </button>
          <div style={styles.callScreenBottomBarCenterStyle}>
            <OpenAiControlBar onHangup={onHangup} />
          </div>
          <div style={styles.callScreenBottomBarRightStyle}>
            <InerviewStepper />
          </div>
        </div>

        <p
          style={{
            margin: '12px 0 0',
            padding: '0 8px',
            fontSize: 12,
            color: '#2a312b',
            lineHeight: 1.4,
            fontFamily: 'G42 Sans',
            fontWeight: 300,
            textAlign: 'center',
            maxWidth: '100%',
            position: 'relative',
            top: 6
          }}
        >
          Agent might take a few seconds pause during the conversation to process inputs or because of connectivity
          issues, if any.
        </p>
      </Stack>

      {showTranscriptPanel && (
        <div style={styles.transcriptPanelStyle}>
          <div style={styles.transcriptPanelHeaderStyle}>
            <h2 style={styles.transcriptPanelTitleStyle}>Live Transcript</h2>
            <button
              type="button"
              style={styles.transcriptPanelCloseStyle}
              onClick={() => setShowTranscriptPanel(false)}
              aria-label="Close transcript"
            >
              <CloseIcon />
            </button>
          </div>
          <div ref={transcriptContentRef} style={styles.transcriptPanelContentStyle}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={msg.sender === 'agent' ? 'agent-question-text-container' : undefined}
                style={styles.transcriptBubbleStyle}
              >
                <p style={styles.transcriptBubbleLabelStyle}>{msg.sender === 'agent' ? 'Agent' : 'You'}</p>
                <p style={{ ...styles.transcriptBubbleTextStyle, whiteSpace: 'pre-wrap' }}>{msg.text}</p>
              </div>
            ))}
            <div style={styles.transcriptListeningStyle}>Listening for your response...</div>
          </div>
        </div>
      )}
    </CallProvider>
  );
};
